import { getScrollableAncestor } from '@/utils/dom/getScrollableAncestor';
import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

// `useLayoutEffect` adjusts scroll before paint (no visible jump) but warns
// under SSR; fall back to `useEffect` on the server.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const offsetInScrollEl = (el: Element, scrollEl: Element): number =>
  el.getBoundingClientRect().top - scrollEl.getBoundingClientRect().top;

/**
 * Keep `anchorRef` at the same on-screen position across a re-render that
 * changes the height of the content around it.
 *
 * Collapsing a long list, for instance, removes rows above the toggle button:
 * the surrounding scroll container's content shrinks while `scrollTop` stays
 * put, so the browser clamps the scroll and the pane appears to jump. Call
 * `capture()` right before triggering the change; once `dependency` changes,
 * the hook shifts the scroll container back so the anchor lands where it was.
 *
 * Assigning past the (possibly smaller) scroll range is harmless — the browser
 * clamps it, which is the closest reachable position.
 */
export const usePreserveScrollAnchor = (
  anchorRef: RefObject<HTMLElement | null>,
  dependency: unknown
): { capture: () => void } => {
  const scrollElRef = useRef<Element | null>(null);
  const savedOffsetRef = useRef<number | null>(null);

  const capture = useCallback(() => {
    const anchor = anchorRef.current;
    const scrollEl = getScrollableAncestor(anchor);
    scrollElRef.current = scrollEl;
    savedOffsetRef.current =
      anchor && scrollEl ? offsetInScrollEl(anchor, scrollEl) : null;
  }, [anchorRef]);

  useIsomorphicLayoutEffect(() => {
    const scrollEl = scrollElRef.current;
    const anchor = anchorRef.current;
    const saved = savedOffsetRef.current;
    savedOffsetRef.current = null;
    if (!scrollEl || !anchor || saved === null) return;

    scrollEl.scrollTop += offsetInScrollEl(anchor, scrollEl) - saved;
    // `anchorRef` is a stable ref object, so this effectively runs only when
    // `dependency` changes — and then only if `capture()` armed it.
  }, [anchorRef, dependency]);

  return { capture };
};
